import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Globe, LogOut, Menu, X, Plus, Sparkles, LayoutDashboard, Compass
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { DashboardStats } from "@/components/DashboardStats";
import { DestinationCard, EmptyDestinations } from "@/components/DestinationCard";

const DashboardPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [trackedCountries, setTrackedCountries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    checkAccessAndLoad();
  }, []);

  const checkAccessAndLoad = async () => {
    setIsLoading(true);
    try {
      const userData = await api.getMe();
      setUser(userData);
      if (!userData.has_core_access) {
        toast.error("Please upgrade to Core Access ($29.99) to view your dashboard");
        navigate("/pricing");
        return;
      }
      const countries = await api.getUserCountries();
      setTrackedCountries(countries);
    } catch (error) {
      navigate("/login");
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    await api.logout();
    navigate("/");
  };

  const handleRemoveCountry = async (ucId) => {
    try {
      await api.deleteUserCountry(ucId);
      setTrackedCountries(trackedCountries.filter(c => c.id !== ucId));
      toast.success("Removed from tracking");
    } catch (error) {
      toast.error("Failed to remove");
    }
  };

  const calculateProgress = (checklist) => {
    if (!checklist || Object.keys(checklist).length === 0) return 0;
    const completed = Object.values(checklist).filter(Boolean).length;
    return Math.round((completed / Object.keys(checklist).length) * 100);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 glass border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Globe className="h-6 w-6 text-primary" />
            <span className="font-bold text-lg font-['Manrope']">Nomad Roadmap</span>
          </div>

          <nav className="hidden md:flex items-center gap-6">
            <Link to="/dashboard" className="text-foreground font-medium flex items-center gap-2">
              <LayoutDashboard className="h-4 w-4" />
              Dashboard
            </Link>
            <Link to="/navigator" className="text-muted-foreground hover:text-foreground transition-colors flex items-center gap-2">
              <Compass className="h-4 w-4" />
              Navigator
            </Link>
            <Link to="/results" className="text-muted-foreground hover:text-foreground transition-colors">
              Explore
            </Link>
            <Link to="/documents" className="text-muted-foreground hover:text-foreground transition-colors">
              Documents
            </Link>
            <Link to="/profile" className="text-muted-foreground hover:text-foreground transition-colors">
              Profile
            </Link>
          </nav>

          <div className="hidden md:flex items-center gap-3">
            {!user?.has_core_access && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/pricing")}
                className="text-accent border-accent hover:bg-accent/10"
                data-testid="upgrade-btn"
              >
                <Sparkles className="h-4 w-4 mr-1" />
                Upgrade
              </Button>
            )}
            <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="logout-btn">
              <LogOut className="h-5 w-5" />
            </Button>
          </div>

          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </div>

        {menuOpen && (
          <div className="md:hidden border-t border-border/50 bg-background p-4 space-y-4 animate-fade-in">
            <Link to="/dashboard" className="block py-2 font-medium">Dashboard</Link>
            <Link to="/navigator" className="block py-2 text-muted-foreground">Navigator</Link>
            <Link to="/results" className="block py-2 text-muted-foreground">Explore</Link>
            <Link to="/documents" className="block py-2 text-muted-foreground">Documents</Link>
            <Link to="/profile" className="block py-2 text-muted-foreground">Profile</Link>
            {!user?.has_core_access && (
              <Button onClick={() => navigate("/pricing")} className="w-full" variant="outline">
                <Sparkles className="h-4 w-4 mr-1" />
                Upgrade
              </Button>
            )}
            <Button onClick={handleLogout} variant="ghost" className="w-full justify-start">
              <LogOut className="h-4 w-4 mr-2" />
              Sign Out
            </Button>
          </div>
        )}
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8" data-testid="dashboard-main">
        <div className="mb-8 animate-fade-in">
          <h1 className="text-2xl md:text-3xl font-bold font-['Manrope'] mb-2">
            Welcome back!
          </h1>
          <p className="text-muted-foreground">
            Manage your visa applications and track your journey.
          </p>
        </div>

        <DashboardStats trackedCountries={trackedCountries} user={user} calculateProgress={calculateProgress} />

        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold font-['Manrope']">Your Destinations</h2>
            <Button
              onClick={() => navigate("/results")}
              variant="outline"
              size="sm"
              data-testid="add-destination-btn"
            >
              <Plus className="h-4 w-4 mr-1" />
              Add More
            </Button>
          </div>

          {trackedCountries.length === 0 ? (
            <EmptyDestinations />
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {trackedCountries.map((country, index) => (
                <DestinationCard
                  key={country.id}
                  country={country}
                  index={index}
                  onRemove={handleRemoveCountry}
                  calculateProgress={calculateProgress}
                />
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default DashboardPage;
